import { Pipe, PipeTransform } from '@angular/core';
import { UserActivityInfoModel } from '../../model';
import { ReviewAggrigatorModel } from '@shared/model/review-aggrigator-model';
import { ActivityPluseAggrigatorModel } from '@shared/model/activity-plus-aggrigator-model';
import { FeedOtherActivitiesPipe } from '@shared/components/Feeds/pipes/feed-other-activities.pipe';
// import { ActivityTextEnum, ActivityTextPipe } from '@shared/pipes/activity-text-pipe/activity-text.pipe';
import { ActivityVerbEnum } from '@shared/model/enum/activity-verb-enum';
import { ObjectTypeEnum } from '@shared/model/enum/object-type-enum';
import { ActivitiesActorAndAffected, ActivitiesAggrigatorModel } from '@shared/model/activities-aggrigator-model';
import { progressModel } from '@shared/model/states/progress.model';
import { ReadTimePipe } from '@shared/pipes/read-time/read-time.pipe';
import { NotificationModel } from '@app/pages/notifications/models/notification-model';
import { CommentsAggrigatorModel } from '@shared/model/comment-aggrigator-model';
import { IClubModel } from "@app/pages/clubs/models/club-model";
import { QuoteModel } from '@app/pages/quotes/models/quote-model';
import { IPostModel } from '@app/pages/posts/models/post.model';
import { BooklistAggrigatorModel } from '@app/pages/booklists/models/booklist-aggrigator-model';
import { IReview } from '@app/pages/reviews/models/review.interface';
import { IClubPreviewModel } from '@app/pages/clubs/models/club-preview-model';
import { UserChallengeModel } from '@app/pages/challenges/models/user-challenge-model';
import { AnnualReportModel, IUserInfo } from '@shared/model/annual-report.model';
import { ActivityDescriptionService } from '@shared/service/activity-text-utils/activity-description.service';
import { TemplateNotificationGeneratorService } from '@shared/service/notificationFactory/template-notification-generator.service';

@Pipe({
  name: 'getUserData',
  standalone: true,
})
export class GetUserDataPipe implements PipeTransform {
  constructor(
    private activityDescriptionService: ActivityDescriptionService,
    private templateNotificationGeneratorService: TemplateNotificationGeneratorService
  ) { }

  transform(
    data: ActivityPluseAggrigatorModel | AnnualReportModel |
      IReview | NotificationModel | BooklistAggrigatorModel | ActivitiesAggrigatorModel | IPostModel | progressModel | QuoteModel | UserChallengeModel | CommentsAggrigatorModel | IUserInfo,
    isAffectedContent = false,
    currentUserId?: string): UserActivityInfoModel {

    const textReadTimePipe = new ReadTimePipe();
    const User: UserActivityInfoModel = new UserActivityInfoModel();
    const feedOtherActivitiesPipe = new FeedOtherActivitiesPipe();


    const keys = Object.getOwnPropertyNames(data ?? {});
    // example: const keys = ['mainFeed', 'actor', 'createdAt'];
    const hasKey = (pattern: string) => keys.some(k => k.includes(pattern));

    const isFeed = hasKey('mainFeed');
    const isReview = hasKey('spoilerAlert');
    const isQuote = hasKey('pageNum');
    const isBookList = hasKey('isStatic');
    const isPostList = hasKey('post');
    const isProgress = hasKey('progress');
    const isNotification = hasKey('sawDate');
    const isComment = hasKey('comment');
    const isIReview = hasKey('mentions');
    const isChallenge = hasKey('sumNegativeScore');
    const isClub = hasKey('access') && !hasKey('actor');
    const isClubPreview = hasKey('joinRequestState') && !isClub && hasKey('access');
    const isAnnualReport = hasKey('isPublished');


    if (isFeed) {
      const activityModel = data as ActivityPluseAggrigatorModel;
      const mainFeed = activityModel.mainFeed;
      // Select user: affectedUser if it's affected content, otherwise actor
      const targetUser = isAffectedContent ? mainFeed.affectedUser : mainFeed.actor;

      // User.activityDate = mainFeed?.inDirectObject?.data?.createdAt || mainFeed?.createdAt;
      User.activityId = mainFeed?.id!;
      User.activityObjectId = mainFeed?.inDirectObject?.objectId;
      User.activityObjectType = mainFeed?.inDirectObject?.objectType;
      User.activityText = this.activityDescriptionService.generateNotification(mainFeed, isAffectedContent);
      User.activityVerb = mainFeed?.verb!;

      User.activityDate = (User.activityVerb === ActivityVerbEnum.Commented && !isAffectedContent)
        ? mainFeed?.carried?.data?.createdAt
        : mainFeed?.inDirectObject?.data?.createdAt || mainFeed?.createdAt;
      User.hasOtherObjects = feedOtherActivitiesPipe.transform(activityModel, isAffectedContent);
      User.includeImageIcon = false;
      User.bookId = mainFeed?.object?.data?.book?.id;
      // Set user info
      this.fillUserInfo(targetUser!, User);
    }

    else if (isNotification) {
      const activityModel = data as NotificationModel;
      const activity = activityModel.activity;

      // Set basic activity info
      User.activityDate = activity?.createdAt!;
      User.activityId = activityModel?.id!;
      User.activityVerb = activity?.verb!;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;

      // Determine objectId based on verb and objectType
      const isCourseActivity =
        activity?.verb === ActivityVerbEnum.Coursed ||
        activity?.verb === ActivityVerbEnum.CourseEnded;

      const isReaction = activity?.verb === ActivityVerbEnum.Reacted;
      const isMessageReaction =
        isReaction && activity?.inDirectObject?.data?.objectType === ObjectTypeEnum.Message;

      if (isCourseActivity) {
        User.activityObjectId = activity?.object?.data?.book?.id;
      } else if (isMessageReaction) {
        User.activityObjectId = activity?.inDirectObject?.data?.parentMessage?.objectId;
      } else if (isReaction) {
        User.activityObjectId = activity?.inDirectObject?.data?.objectId;
      } else {
        User.activityObjectId = activity?.inDirectObject?.objectId;
      }

      //  Set object type
      User.activityObjectType = isMessageReaction
        ? activity?.inDirectObject?.data?.parentMessage?.objectType
        : activity?.inDirectObject?.data?.objectType || activity?.inDirectObject?.objectType;

      //  Set text and like info
      User.activityText = this.templateNotificationGeneratorService.generateNotification(activity!, currentUserId!);

      User.reactedId = activity?.inDirectObject?.objectId;
      User.totalLikesCount =
        activity?.verb !== ActivityVerbEnum.Reacted
          ? activity?.totalLikes
          : activity?.carried?.data?.groupedUserReactionForThisMessage[0]?.count;

      //  Set user info
      const targetUser = isAffectedContent ? activity?.affectedUser : activity?.actor;
      this.fillUserInfo(targetUser!, User);
    }

    else if (isReview) {
      const activityModel = data as ReviewAggrigatorModel;
      User.activityDate = activityModel?.review?.data?.createdAt!;
      User.activityId = activityModel?.id;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Review;
      User.activityText = '';
      User.activityVerb = ActivityVerbEnum.Reviewed;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isQuote) {
      const activityModel = data as QuoteModel;
      User.activityDate = activityModel?.createdAt!;
      User.activityId = activityModel?.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Quote;
      User.activityText = '';
      User.activityVerb = ActivityVerbEnum.Quoted;
      User.hasOtherObjects = false;
      User.includeImageIcon = false!;
      User.pageNum = activityModel.pageNum!;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isBookList) {
      const activityModel = data as BooklistAggrigatorModel;
      User.activityDate = activityModel.createdAt!;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Booklist;
      User.activityVerb = ActivityVerbEnum.Booklisted;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      this.fillUserInfo(activityModel.actor!, User);
      // ? Just Users can create a book list ;
      User.userType = 1; // override default type
    }
    else if (isPostList) {
      const activityModel = data as IPostModel;
      User.activityDate = activityModel.createdAt!;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Post;
      User.activityText = '';
      User.activityVerb = ActivityVerbEnum.Posted;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      User.textReadTime = !isAffectedContent ? textReadTimePipe.transform(activityModel?.body!) : '';
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isProgress) {
      const activityModel = data as progressModel;
      const notifModel = this.mapProgressToActivity(data as progressModel);
      User.activityDate = activityModel.progress?.data.createdAt;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel.id;
      User.activityObjectType = activityModel.progress?.objectType;
      User.activityText = this.activityDescriptionService.generateNotification(notifModel, isAffectedContent);
      User.activityVerb = ActivityVerbEnum.ReadingProgressed;
      User.includeImageIcon = false;
      User.hasOtherObjects = false;
      User.bookId = activityModel.progress?.data.bookId;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isComment) {
      const activityModel = data as CommentsAggrigatorModel;
      User.activityDate = activityModel.comment.data.createdAt!;
      User.activityId = activityModel?.id;
      User.activityObjectId = activityModel?.comment.objectId;
      User.activityObjectType = activityModel?.comment.objectType;
      User.activityText = '';
      User.activityVerb = ActivityVerbEnum.Commented;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      User.hasReply = activityModel?.comment?.data?.mentions?.length! > 0;
      this.fillUserInfo(activityModel.actor, User);
    }
    else if (isIReview) {
      const activityModel = data as IReview;
      User.activityDate = activityModel?.review?.data?.createdAt!;
      User.activityId = activityModel?.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Review;
      User.activityText = ''
      User.activityVerb = ActivityVerbEnum.Reviewed;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isClubPreview) {
      const activityModel = data as IClubPreviewModel;
      User.activityDate = activityModel?.createdAt?.toString()!;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isClub) {
      const activityModel = data as IClubModel;
      User.activityDate = activityModel?.createdAt?.toString()!;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.Club;
      User.activityVerb = ActivityVerbEnum.Booklisted;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      this.fillUserInfo(activityModel, User);
      User.userType = 5; // override default type
    }
    else if (isChallenge) {
      const activityModel = data as UserChallengeModel;
      User.activityDate = activityModel?.createdAt?.toString()!;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel?.challengeId;
      User.activityObjectType = ObjectTypeEnum.ChallengeV2;
      User.activityVerb = ActivityVerbEnum.ChallengeObtained;
      User.hasOtherObjects = false;
      User.includeImageIcon = false!;
      this.fillUserInfo(activityModel.actor!, User);
    }
    else if (isAnnualReport) {
      const activityModel = data as AnnualReportModel;
      User.activityDate = activityModel?.createdAt?.toString()!;
      User.activityId = activityModel.id!;
      User.activityObjectId = activityModel?.id;
      User.activityObjectType = ObjectTypeEnum.ChallengeV2;
      User.activityVerb = ActivityVerbEnum.ChallengeObtained;
      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      this.fillUserInfo(activityModel.userInfo!, User);
    }
    else {
      // Default handling for profile and club activities
      const activityModel = data as ActivitiesAggrigatorModel;

      // Determine the user based on affectedContent flag
      const targetUser = isAffectedContent ? activityModel.affectedUser : activityModel.actor;

      // Set basic activity info
      // User.activityDate = activityModel?.inDirectObject?.data?.createdAt || activityModel?.createdAt;
      User.activityId = activityModel?.id!;

      // Determine object id depending on verb type
      const isCourseActivity =
        activityModel?.verb === ActivityVerbEnum.Coursed ||
        activityModel?.verb === ActivityVerbEnum.CourseEnded;

      User.activityObjectId = isCourseActivity
        ? activityModel?.object?.data?.book?.id
        : activityModel?.inDirectObject?.objectId;

      User.activityObjectType = activityModel?.inDirectObject?.objectType;
      User.activityText = this.activityDescriptionService.generateNotification(activityModel, isAffectedContent);
      User.activityVerb = activityModel?.verb!;

      User.activityDate = (User.activityVerb === ActivityVerbEnum.Commented && !isAffectedContent)
        ? activityModel?.carried?.data?.createdAt
        : activityModel?.inDirectObject?.data?.createdAt || activityModel?.createdAt;

      User.hasOtherObjects = false;
      User.includeImageIcon = false;
      User.affectedUser = activityModel?.affectedUser;
      // Set bookId if available
      User.bookId = activityModel?.object?.data?.book?.id;

      // Set user info
      this.fillUserInfo(targetUser!, User);
    }
    return User;
  }


  private fillUserInfo(targetUser: ActivitiesActorAndAffected, user: UserActivityInfoModel) {
    user.userFullname = targetUser?.fullName ?? targetUser?.name ?? '';
    user.userHasPhoto = targetUser?.hasPhoto ?? false;
    user.userId = targetUser?.id ?? '';
    user.userName = targetUser?.userName ?? '';
    user.userType = targetUser?.type ?? 1;
  }




  mapProgressToActivity(progress: progressModel): ActivitiesAggrigatorModel {
    const activity = new ActivitiesAggrigatorModel();
    activity.id = progress.id;
    activity.actor = progress.actor;

    activity.inDirectObject = {
      data: progress.progress?.data
    } as any;

    const steps = progress.progress?.data?.steps ?? 0;
    const totalPages = progress.progress?.data?.totalPages ?? 0;

    if (steps === -1) {
      activity.verb = ActivityVerbEnum.StateChangedIWantToRead;
    } else if (steps === totalPages && totalPages > 0) {
      activity.verb = ActivityVerbEnum.StateChangedToRead;
    } else if (steps > 0 && steps < totalPages) {
      activity.verb = ActivityVerbEnum.ReadingProgressed;
    } else {
      activity.verb = ActivityVerbEnum.StateChangedToCurrently;
    }

    return activity;
  }
}
